import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, CircleDot } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_DOTS = { high: "#d34949", medium: "#d9a05b", low: "#7e907a" };
const priorityDot = (p) => PRIORITY_DOTS[p] || PRIORITY_DOTS.low;

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    due_date: "",
    assignee_id: "",
  });

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));

  useEffect(() => {
    load();
    if (user?.role === "admin") {
      api.get("/employees").then((r) => setEmployees(r.data));
    }
  }, [user?.role]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/tasks/${id}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", {
        ...form,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
      });
      setOpen(false);
      setForm({ title: "", description: "", priority: "medium", due_date: "", assignee_id: "" });
      toast.success("Task created");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <header className="flex items-end justify-between">
        <div>
          <div className="overline">Work</div>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">
            Tasks
          </h1>
          <p className="mt-2 text-sm text-[#575752]">
            {tasks.length} tasks in the pipeline.
          </p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-task-btn"
                className="bg-[#4a6b53] text-white hover:bg-[#3d5a45]"
              >
                <Plus strokeWidth={1.5} className="mr-1.5 h-4 w-4" /> New task
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Create task</DialogTitle>
              </DialogHeader>
              <form onSubmit={createTask} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="t-title">Title</Label>
                  <Input
                    id="t-title"
                    data-testid="task-title-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-desc">Description</Label>
                  <Textarea
                    id="t-desc"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Priority</Label>
                    <Select
                      value={form.priority}
                      onValueChange={(v) => setForm({ ...form, priority: v })}
                    >
                      <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="t-due">Due date</Label>
                    <Input
                      id="t-due"
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Assignee</Label>
                  <Select
                    value={form.assignee_id}
                    onValueChange={(v) => setForm({ ...form, assignee_id: v })}
                  >
                    <SelectTrigger data-testid="task-assignee-select">
                      <SelectValue placeholder="Choose an employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} — {e.role_title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="task-create-submit"
                    className="bg-[#4a6b53] text-white hover:bg-[#3d5a45]"
                  >
                    Create task
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="overflow-hidden rounded-lg border border-[#e5e5e0] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[44px]" />
              <TableHead>Task</TableHead>
              <TableHead>Assignee</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="w-[160px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((t) => (
              <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
                <TableCell>
                  <CircleDot
                    className="h-3.5 w-3.5"
                    style={{ color: priorityDot(t.priority) }}
                    strokeWidth={2}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium text-[#1c1c19]">{t.title}</div>
                  {t.description ? (
                    <div className="mt-0.5 line-clamp-1 text-xs text-[#575752]">
                      {t.description}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="text-sm text-[#575752]">
                  {t.assignee_name || "Unassigned"}
                </TableCell>
                <TableCell className="font-mono text-xs text-[#575752]">
                  {t.due_date || "—"}
                </TableCell>
                <TableCell>
                  <Select
                    value={t.status}
                    onValueChange={(v) => setStatus(t.id, v)}
                  >
                    <SelectTrigger data-testid={`task-status-${t.id}`} className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">Todo</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-sm text-[#575752]">
                  No tasks yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
